// src/components/SubdomainEntry.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getInstitutionBySubdomain } from '../services/api';
import { BuildingLibraryIcon } from '@heroicons/react/24/outline';

function SubdomainEntry({ onInstitutionSelected }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [subdomainInput, setSubdomainInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const inputRef = React.useRef(null);

  // Set page title
  useEffect(() => {
    document.title = t('subdomain.selectInstitution');
  }, [t]);

  // Auto-focus the input field on component mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedSubdomain = subdomainInput.trim().toLowerCase();
    
    if (!trimmedSubdomain) {
      setError(t('subdomain.enterSubdomain'));
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await getInstitutionBySubdomain(trimmedSubdomain);
      
      if (response.success && response.data) {
        if (response.data.registration_status !== 'active') {
          setError(t('subdomain.notActive', { name: response.data.name || trimmedSubdomain }));
          setIsLoading(false);
          return;
        }
        onInstitutionSelected(response.data);
        navigate('/login');
      } else {
        setError(response.message || t('subdomain.notFound'));
      }
    } catch (apiError) {
      setError(apiError.message || t('subdomain.apiError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-slate-100 to-sky-100 p-4 selection:bg-cyan-500 selection:text-white">
      <div className="w-full max-w-md">
        <div className="bg-white p-8 rounded-xl shadow-2xl border border-slate-200/80">
          <div className="text-center mb-8">
            <BuildingLibraryIcon className="w-16 h-16 mx-auto text-cyan-600 mb-4" />
            <h1 className="text-3xl font-bold text-slate-800">
              {t('subdomain.title')}
            </h1>
            <p className="text-slate-500 mt-2">
              {t('subdomain.subtitle')}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm text-center transition-all duration-300 ease-in-out">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="subdomain" className="block text-sm font-medium text-slate-700 sr-only">
                {t('subdomain.subdomainLabel')}
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <input
                  ref={inputRef}
                  type="text"
                  name="subdomain"
                  id="subdomain"
                  value={subdomainInput}
                  onChange={(e) => setSubdomainInput(e.target.value)}
                  className="block w-full px-5 py-3.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-lg sm:text-base placeholder-slate-400 text-center transition-colors duration-200 ease-in-out"
                  placeholder={t('subdomain.subdomainPlaceholder')}
                  required
                  disabled={isLoading}
                  autoCapitalize="none"
                  autoCorrect="off"
                />
              </div>
              <p className="mt-3 text-xs text-slate-500 text-center">
                {t('subdomain.subdomainHint')}
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-gradient-to-r from-cyan-600 to-blue-500 hover:from-cyan-700 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-60 transition duration-150 ease-in-out"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('subdomain.loading')}
                </>
              ) : (
                t('subdomain.accessPortal')
              )}
            </button>
          </form>
        </div>
        
        <p className="mt-8 text-center text-sm text-slate-500">
          {t('subdomain.contactAdmin')}
        </p>
      </div>
    </div>
  );
}

export default SubdomainEntry;